import { PlatformLocation } from '@angular/common';
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

export interface SeoData {
  title?: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  imageAlt?: string;
  keywords?: string[];
  robots?: string;
  type?: 'website' | 'product';
  jsonLd?: JsonLd;
}

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  private readonly defaultTitle = "Romantic Lace Women's Clothing";
  private readonly defaultDescription =
    "Shop Lace & Liora, an independent women's lace clothing boutique for romantic lace tops, dresses, and effortless styles made for everyday confidence.";
  private readonly defaultImage = `${environment.host}/images/home/banner.webp`;
  private readonly logo = `${environment.host}/images/logo512.webp`;
  private readonly language = 'en-US';
  private readonly organizationId = `${environment.host}/#organization`;
  private readonly websiteId = `${environment.host}/#website`;

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private dom: Document,
    private platformLocation: PlatformLocation
  ) { }

  setPageMeta(data: SeoData = {}, currentPath?: string) {
    const title = this.buildTitle(data.title);
    const description = this.truncateMeta(
      data.description ?? this.defaultDescription
    );
    const canonicalPath =
      data.canonicalPath ?? currentPath ?? this.platformLocation.pathname;
    const canonicalUrl = this.absoluteUrl(canonicalPath);
    const image = this.absoluteUrl(data.image ?? this.defaultImage);
    const imageAlt = data.imageAlt ?? title;
    const robots = data.robots ?? 'index, follow';
    const type = data.type ?? 'website';
    const hasCustomJsonLd = data.jsonLd !== undefined;

    this.title.setTitle(title);
    this.setCanonicalURL(canonicalPath);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ name: 'theme-color', content: '#f5eef0' });
    this.meta.updateTag({ name: 'application-name', content: environment.appname });
    this.meta.updateTag({
      name: 'apple-mobile-web-app-title',
      content: environment.appname,
    });
    this.meta.updateTag({
      name: 'keywords',
      content: (data.keywords ?? this.defaultKeywords()).join(', '),
    });

    this.meta.updateTag({ property: 'og:site_name', content: environment.appname });
    this.meta.updateTag({
      property: 'og:locale',
      content: this.language.replace('-', '_'),
    });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: type });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:secure_url', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: imageAlt });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: imageAlt });

    const jsonLd = [
      ...this.toJsonLdArray(
        this.defaultJsonLd(canonicalUrl, title, description, image)
      ),
      ...this.toJsonLdArray(
        this.breadcrumbJsonLd(canonicalUrl, canonicalPath, data.title, robots, hasCustomJsonLd)
      ),
      ...(hasCustomJsonLd ? this.toJsonLdArray(data.jsonLd as JsonLd) : []),
    ];

    this.setJsonLd('page-json-ld', jsonLd);
  }

  setCanonicalURL(path?: string) {
    const canonicalUrl = this.absoluteUrl(
      path ?? this.platformLocation.pathname
    );

    let link = this.dom.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(link);
    }

    link.setAttribute('href', canonicalUrl);
  }

  setTitle(title?: string) {
    this.title.setTitle(this.buildTitle(title));
  }

  setJsonLd(id: string, data: JsonLd | null) {
    const scriptId = `seo-${id}`;
    const existing = this.dom.getElementById(scriptId);

    if (!data) {
      existing?.remove();
      return;
    }

    const script = existing ?? this.dom.createElement('script');
    script.id = scriptId;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(data);

    if (!existing) {
      this.dom.head.appendChild(script);
    }
  }

  buildTitle(title?: string) {
    if (!title) {
      return `${environment.appname} - ${this.defaultTitle}`;
    }

    return `${title} - ${environment.appname}`;
  }

  absoluteUrl(pathOrUrl: string) {
    if (pathOrUrl.startsWith('http')) {
      return pathOrUrl;
    }

    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    const cleanPath = path.split('?')[0].split('#')[0] || '/';
    return `${environment.host}${cleanPath}`;
  }

  private defaultKeywords() {
    return [
      environment.appname,
      "women's fashion",
      "women's lace clothing",
      'lace tops',
      'lace dresses',
      'romantic clothing',
      'dresses',
      'new arrivals',
      'boutique fashion',
    ];
  }

  private defaultJsonLd(
    url: string,
    title: string,
    description: string,
    image: string
  ): JsonLd {
    const address = {
      '@type': 'PostalAddress',
      streetAddress: environment.address1,
      addressLocality: 'Denver',
      addressRegion: 'CO',
      postalCode: '80210',
      addressCountry: 'US',
    };

    return [
      {
        '@context': 'https://schema.org',
        '@type': ['Organization', 'OnlineStore'],
        '@id': this.organizationId,
        name: environment.appname,
        url: environment.host,
        logo: {
          '@type': 'ImageObject',
          url: this.logo,
          width: 512,
          height: 512,
        },
        image: this.defaultImage,
        sameAs: this.compact([environment.social_media.ig]),
        email: environment.email,
        address,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: environment.email,
          availableLanguage: ['English'],
        },
        foundingDate: '2024',
        knowsAbout: [
          "women's lace clothing",
          'romantic lace tops',
          'lace dresses',
          'boutique fashion',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': this.websiteId,
        name: environment.appname,
        url: environment.host,
        publisher: {
          '@id': this.organizationId,
        },
        inLanguage: this.language,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${environment.host}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: title,
        description,
        url,
        isPartOf: {
          '@id': this.websiteId,
        },
        about: {
          '@id': this.organizationId,
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: image,
        },
        inLanguage: this.language,
      },
    ];
  }

  private breadcrumbJsonLd(
    canonicalUrl: string,
    canonicalPath: string,
    title: string | undefined,
    robots: string,
    hasCustomJsonLd: boolean
  ): JsonLd | null {
    const path = this.cleanPath(canonicalPath);
    if (path === '/' || hasCustomJsonLd || !this.isIndexable(robots)) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: environment.host,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: title ?? this.humanizePath(path),
          item: canonicalUrl,
        },
      ],
    };
  }

  private toJsonLdArray(data: JsonLd | null): Record<string, unknown>[] {
    if (!data) {
      return [];
    }

    return Array.isArray(data) ? data : [data];
  }

  private cleanPath(pathOrUrl: string) {
    if (pathOrUrl.startsWith('http')) {
      try {
        const url = new URL(pathOrUrl);
        return url.pathname || '/';
      } catch {
        return '/';
      }
    }

    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return path.split('?')[0].split('#')[0] || '/';
  }

  private humanizePath(path: string) {
    return path
      .split('/')
      .filter(Boolean)
      .pop()
      ?.replace(/-/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()) ?? environment.appname;
  }

  private isIndexable(robots: string) {
    return !robots.toLowerCase().includes('noindex');
  }

  private truncateMeta(value: string, maxLength: number = 160) {
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) {
      return normalized;
    }

    return `${normalized.slice(0, maxLength - 3).trim()}...`;
  }

  private compact<T>(values: (T | null | undefined | '')[]): T[] {
    return values.filter(Boolean) as T[];
  }
}
